/**
 * The permission vocabulary as people should see it.
 *
 * The server deliberately uses terse, stable keys such as `charts:write`.
 * Those are useful in code and audit records, but they are not useful answers
 * to "what can I do?". When the server has an answer (`/api/me` `access`, from
 * the evaluator its guards use) that answer is shown, with the reason for each
 * line - Features can block what a role allows. The static list below is the
 * fallback before that answer lands.
 */

import type { CapabilityDecision } from '../../api/types.ts';

export interface AccessCapability {
  permission: string;
  label: string;
}

export interface AccessGroup {
  label: string;
  capabilities: readonly AccessCapability[];
}

export interface ResolvedAccessCapability extends AccessCapability {
  available: boolean;
  /** Why, when the server said: a role, a Features decision, or what is missing. */
  reason?: string;
}

export interface ResolvedAccessGroup {
  label: string;
  capabilities: readonly ResolvedAccessCapability[];
}

export const ACCESS_GROUPS: readonly AccessGroup[] = [
  {
    label: 'Explore & SONAR',
    capabilities: [
      { permission: 'explore:query', label: 'Explore and preview data' },
      { permission: 'explore:export', label: 'Export data' },
      { permission: 'assistant:use', label: 'Use SONAR' },
      { permission: 'assistant:audit', label: "Review other users' SONAR conversations" },
    ],
  },
  {
    label: 'Saved charts',
    capabilities: [
      { permission: 'charts:read', label: 'View saved charts' },
      { permission: 'charts:write', label: 'Save and edit charts' },
      { permission: 'charts:share', label: 'Share charts with others' },
      { permission: 'charts:delete', label: 'Delete saved charts' },
    ],
  },
  {
    label: 'Dashboards & folders',
    capabilities: [
      { permission: 'dashboards:read', label: 'View dashboards' },
      { permission: 'dashboards:write', label: 'Create and edit dashboards' },
      { permission: 'dashboards:share', label: 'Share dashboards with others' },
      { permission: 'dashboards:delete', label: 'Delete dashboards' },
      { permission: 'folders:read', label: 'Browse dashboard folders' },
      { permission: 'folders:write', label: 'Organise dashboard folders' },
      { permission: 'folders:delete', label: 'Delete dashboard folders' },
    ],
  },
  {
    label: 'Argus',
    capabilities: [
      { permission: 'argus:read', label: 'Open Argus and read alerts and rules' },
      { permission: 'argus:triage', label: 'Triage Argus alerts' },
      { permission: 'argus:author', label: 'Write Argus rules' },
      { permission: 'argus:deploy', label: 'Deploy Argus rules' },
    ],
  },
  {
    label: 'Administration',
    capabilities: [
      { permission: 'users:read', label: 'View people and groups' },
      { permission: 'users:write', label: 'Manage people, groups, and access' },
      { permission: 'flags:read', label: 'View feature availability' },
      { permission: 'flags:write', label: 'Manage feature availability' },
      { permission: 'audit:read', label: 'View activity history' },
    ],
  },
];

/**
 * Resolve every capability, including the ones this account does not hold.
 * With server decisions, those win: they already fold in Features.
 */
export function resolveAccessCapabilities(
  permissions: ReadonlySet<string>,
  isRoot: boolean,
  decisions?: readonly CapabilityDecision[] | null,
): readonly ResolvedAccessGroup[] {
  if (decisions?.length) {
    const groups = [...new Set(decisions.map((d) => d.group))];
    return groups.map((label) => ({
      label,
      capabilities: decisions.filter((d) => d.group === label).map((d) => ({
        permission: d.key,
        label: d.label,
        available: d.allowed,
        reason: d.reason,
      })),
    }));
  }
  return ACCESS_GROUPS.map((group) => ({
    label: group.label,
    capabilities: group.capabilities.map((capability) => ({
      ...capability,
      available: isRoot || permissions.has(capability.permission),
    })),
  }));
}
